import { TranslateService } from '@ngx-translate/core';
import { AsgardTranslateService } from './translate/asgard-translate.service';
import { MenuTabService } from './menu-tab.service';
import { EventEmitter, Injectable, NgZone, Output } from '@angular/core';
import { PaneService } from './pane.service';
import { ModalWindowService } from './ui/modal-window.service';
import { CurrentUiStoreService } from './ui/current-ui-store.service';
import { UploadFileComponent } from '../components/upload-file/upload-file.component';
import { ACCEPTED_FORMATS, UPLOAD_TYPES } from 'src/models/UploadFileTypes';
import { ReadingToolbarComponent } from '../components/reading-toolbar/reading-toolbar.component';
import { IMenuItemAction, RootMenuItem } from 'src/models/Menu';
import { AsgardFeatureNames, featureIcons } from 'src/models/AsgardFeatures';
import { AuthService } from './auth/auth.service';
import { PrintService } from './print.service';
import { SpellCheckService } from './spell-check/spell-check.service';
import { WordPredictionService } from './word-prediction/word-prediction.service';
import { TabService } from './tab.service';
import { SaveService } from './save.service';
import { FileConvertorService } from './file-convertor/file-convertor-service';
import { FileActionEnum, ToastrActionEnum } from '../utils/enums';
import { TabNames } from '../core/enums';
import { SettingsComponent } from '../components/settings/settings.component';
import { LookupComponent } from '../components/lookup/lookup.component';
import { CloudDlgManagerService } from './cloud-storage/cloud-dlg-manager.service';
import { SaveStatusEnum } from './cloud-storage/enums/save-status.enum';
import { DocumentManagerService } from './document/document-manager.service';
import { AsgardDocPdf } from '../core/documents/asgard-doc-pdf';
import { AsgardDocFroala } from '../core/documents/asgard-doc-froala';
import { isK3000domain } from '../core/common';
import { PdfPageSelectDialogComponent } from '../components/dialogs/pdf-page-select-dialog/pdf-page-select-dialog.component';
import { ScreenshotService } from './screenshot.service';
import { CloudServiceEnum } from './cloud-storage/enums/cloud-service.enum';
import { FileManagerDialogComponent } from '../components/dialogs/file-manager-dialog/file-manager-dialog.component';
import { AudioMakerService } from './audio-maker/audio-maker.service';
import { AutosaveManagerService } from './autosave/autosave-manager.service';
import { HtmlToRtfConversion, PdfToKesConversion } from './file-convertor/conversions';
import { ResponsiveUIBreakpoints } from '../utils/enums/responsive-ui-breakpoints.enum';
import { ExamModeRestrictionsService } from './exam/exam-mode-restrictions.service';
import { ExamCreationService } from './exam/exam-creation.service';
import { AccessCodeDialogComponent } from '../components/dialogs/access-code-dialog/access-code-dialog.component';
import { ExamModeService } from './exam/exam-mode.service';
import { MessageDialogActionColor, MessageDialogComponent, MessageDialogData } from '../components/dialogs/message-dialog/message-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { OverviewExamsComponent } from '../components/examMode/overview-exams/overview-exams.component';
import { CompletedExamPreviewDialogComponent } from '../components/dialogs/completed-exam-preview-dialog/completed-exam-preview-dialog.component';
import { PlatformDetectionService } from './platform-detection.service';
import { ToastrDialogService } from './toastr-dialog.service';
import { OpenFileAction } from './open-file.service';
// import { DodpStorageDialogComponent } from '../components/dialogs/dodp-storage-dialog/dodp-storage-dialog.component';
import { AsgardDocEpub } from '../core/documents/asgard-doc-epub';
import { StiDialogComponent } from '../components/dialogs/sti-dialog/sti-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  @Output() menuItemsUpdated: EventEmitter<any> = new EventEmitter();

  MainMenuElements: RootMenuItem[] = [];
  KESMenuElements: RootMenuItem[] = [];
  ToolsMenuElements: RootMenuItem[] = [];
  LangsMenuItems: RootMenuItem[] = [];
  ExamModeItems: RootMenuItem;
  SettingsMenuItems: RootMenuItem[] = [];
  ToolbarMenuItems: RootMenuItem[] = [];
  TextToolsMenuItems: RootMenuItem[] = [];
  HighlightsMenuItems: RootMenuItem[] = [];
  EpubAnnotationsMenuItems: RootMenuItem[] = [];
  SRMenuItems: RootMenuItem[] = [];
  DrawingToolsMenuItems: RootMenuItem[] = [];
  ReadingControlMenuItems: RootMenuItem[] = [];
  ScreenshotReaderMenuItems: RootMenuItem[] = [];
  CollapsedFeaturesMenuItems: RootMenuItem[] = [];
  FeaturesToCollapse: AsgardFeatureNames[] = [
    AsgardFeatureNames.TOOLBAR_MENU,
    AsgardFeatureNames.DRAWING_TOOLS,
    AsgardFeatureNames.HIGHLIGHT_MENU,
    AsgardFeatureNames.EPUB_MENU
  ];

  // for small screens
  private normalMainMenuElements: RootMenuItem[];
  private smallMainMenuElements: RootMenuItem[];
  private filesMenuItem: RootMenuItem;
  private filesMenuElements: RootMenuItem[] = [];
  private ocrMenuItem: RootMenuItem;
  private ocrMenuElements: RootMenuItem[] = [];
  private searchMenuItem: RootMenuItem;
  private toolsMenuItem: RootMenuItem;
  private displaySmallMainMenu: boolean = false;
  private displaySmallMenuIconOnly: boolean = false;

  // all menu items
  private newTabMenuItem: RootMenuItem;
  private openDriveMenuItem: RootMenuItem;
  private saveToDriveMenuItem: RootMenuItem;
  private saveAsToDriveMenuItem: RootMenuItem;
  private openDocMenuItem: RootMenuItem;
  private downloadMenuItem: RootMenuItem;
  private printMenuItem: RootMenuItem;
  private closeDocumentMenuItem: RootMenuItem;
  private openStiMenuItem: RootMenuItem;
  // private openDodpMenuItem: RootMenuItem;


  private get AllMenus() {
    return [...this.MainMenuElements.concat(this.ToolsMenuElements).concat(this.LangsMenuItems).concat(this.SettingsMenuItems).concat(this.ToolbarMenuItems)
      .concat(this.TextToolsMenuItems).concat(this.HighlightsMenuItems).concat(this.EpubAnnotationsMenuItems).concat(this.SRMenuItems)
      .concat(this.DrawingToolsMenuItems).concat(this.ReadingControlMenuItems).concat(this.ScreenshotReaderMenuItems)
      .concat(this.searchMenuItem).concat(this.filesMenuItem).concat(this.filesMenuElements)
      .concat(this.ocrMenuItem).concat(this.ocrMenuElements)
      .concat(this.toolsMenuItem)];
  }

  constructor(
    public paneService: PaneService,
    private modalService: ModalWindowService,
    public uiService: CurrentUiStoreService,
    private saveService: SaveService,
    private authService: AuthService,
    private printService: PrintService,
    private spellCheck: SpellCheckService,
    private convertorService: FileConvertorService,
    private wordPrediction: WordPredictionService,
    private audioMakerService: AudioMakerService,
    private menuTabService: MenuTabService,
    private tabService: TabService,
    private asgardTranslateService: AsgardTranslateService,
    private readonly docMgrService: DocumentManagerService,
    private cloudManager: CloudDlgManagerService,
    private translate: TranslateService,
    private screenshotSvc: ScreenshotService,
    private ngZone: NgZone,
    private readonly autosave: AutosaveManagerService,
    private readonly examModeRestrictionsService: ExamModeRestrictionsService,
    private readonly examCreationService: ExamCreationService,
    private readonly examModeService: ExamModeService,
    private readonly dialog: MatDialog,
    private readonly platform: PlatformDetectionService,
    private readonly toastrService: ToastrDialogService,
  ) {
    this.init();

    // menu items
    this.newTabMenuItem = {
      dynamic: false,
      title: 'MAINMENU.NEW',
      click: this.openTab,
      feature: AsgardFeatureNames.NEW_TAB,
      icon: featureIcons.get(AsgardFeatureNames.NEW_TAB),
      expandable: false,
      isChild: true,
    }

    this.openDriveMenuItem = {
      dynamic: false,
      title: 'MAINMENU.OPENDRIVE',
      click: this.openFromDrive,
      feature: AsgardFeatureNames.OPEN_DRIVE,
      icon: featureIcons.get(AsgardFeatureNames.OPEN_DRIVE),
      expandable: false,
      isChild: true
    }

    this.saveToDriveMenuItem = {
      dynamic: false,
      title: 'MAINMENU.SAVETODRIVE',
      click: this.saveToDrive,
      feature: AsgardFeatureNames.SAVE_TO_DRIVE,
      icon: featureIcons.get(AsgardFeatureNames.SAVE_TO_DRIVE),
      expandable: false,
      isChild: true
    }

    this.saveAsToDriveMenuItem = {
      dynamic: false,
      title: 'MAINMENU.SAVEASTODRIVE',
      click: this.saveAsToDrive,
      feature: AsgardFeatureNames.SAVE_AS_TO_DRIVE,
      icon: featureIcons.get(AsgardFeatureNames.SAVE_AS_TO_DRIVE),
      expandable: false,
      isChild: true
    }

    this.openDocMenuItem = {
      dynamic: true, feature: AsgardFeatureNames.OPEN_DOC, component: UploadFileComponent,
      componentData: {
        UPLOAD_TYPES: UPLOAD_TYPES.PDF,
        ACCEPTED_FORMATS: isK3000domain() ? ACCEPTED_FORMATS.K3000 : ACCEPTED_FORMATS.COMMON,
        ACTION: OpenFileAction.OPEN
      },
      expandable: false,
      isChild: true
    }

    this.downloadMenuItem = {
      dynamic: false,
      title: 'MAINMENU.DOWNLOAD',
      click: this.downloadDoc,
      feature: AsgardFeatureNames.DOWNLOAD,
      icon: featureIcons.get(AsgardFeatureNames.DOWNLOAD),
      expandable: false,
      isChild: true
    }

    this.printMenuItem = {
      dynamic: false, title: 'MAINMENU.PRINT', click: this.printDocument, feature: AsgardFeatureNames.PRINT,
      icon: featureIcons.get(AsgardFeatureNames.PRINT),
      expandable: false,
      isChild: true
    }

    this.closeDocumentMenuItem = {
      dynamic: false,
      title: 'MAINMENU.CLOSEDOCUMENT',
      click: this.closeCurrentDocument,
      feature: AsgardFeatureNames.CLOSE_DOCUMENT,
      icon: featureIcons.get(AsgardFeatureNames.CLOSE_DOCUMENT),
      expandable: false,
      isChild: true,
      style: {
        width: ResponsiveUIBreakpoints.settings - 1,
        action: IMenuItemAction.showinburger
      }
    }

    // this.openDodpMenuItem = {
    //   dynamic: false,
    //   title: 'MAINMENU.OPENDODP',
    //   click: this.openFromDodp,
    //   feature: AsgardFeatureNames.OPEN_DODP,
    //   icon: featureIcons.get(AsgardFeatureNames.OPEN_DODP),
    //   expandable: false,
    //   isChild: true
    // }

    this.openStiMenuItem = {
      dynamic: false,
      title: 'MAINMENU.OPENSTI',
      click: this.openFromSti,
      feature: AsgardFeatureNames.OPEN_STI,
      image: '/assets/images/mijnboekenkluis-logo.png',
      expandable: false,
      isChild: true
    }

    this.KESMenuElements = [
      {
        dynamic: false,
        title: 'MAINMENU.EXPORTKES',
        click: this.exportPdfToKes,
        feature: AsgardFeatureNames.EXPORT_KES,
        icon: featureIcons.get(AsgardFeatureNames.EXPORT_KES),
        expandable: false,
        isChild: true
      },
      {
        dynamic: false,
        title: 'MAINMENU.EXPORTKESDRIVE',
        click: this.exportKesToDrive,
        feature: AsgardFeatureNames.EXPORT_KES_DRIVE,
        icon: featureIcons.get(AsgardFeatureNames.EXPORT_KES_DRIVE),
        expandable: false,
        isChild: true
      },
    ];
    this.ToolsMenuElements = [
      {
        dynamic: false, title: 'TOOLSMENU.AUDIOMAKER', click: this.audioMakerService.toggleAudioMaker,
        feature: AsgardFeatureNames.AUDIO_MAKER, icon: featureIcons.get(AsgardFeatureNames.AUDIO_MAKER),
        expandable: false,
        isChild: true,
        style: {
          width: ResponsiveUIBreakpoints.ToolsMenu,
          action: IMenuItemAction.showinburger
        }
      },
      {
        dynamic: false, title: 'TOOLSMENU.WORDPRED', click: this.wordPrediction.toggleWordPredictor,
        feature: AsgardFeatureNames.WORD_PREDICTION, icon: featureIcons.get(AsgardFeatureNames.WORD_PREDICTION),
        expandable: false,
        isChild: true,
        style: {
          width: ResponsiveUIBreakpoints.ToolsMenu,
          action: IMenuItemAction.showinburger
        }
      },
      {
        dynamic: false, title: 'TOOLSMENU.SPELLCHECK', click: this.spellCheck.toggleSpellCheck,
        feature: AsgardFeatureNames.SPELL_CHECK, icon: featureIcons.get(AsgardFeatureNames.SPELL_CHECK),
        expandable: false,
        isChild: true,
        style: {
          width: ResponsiveUIBreakpoints.ToolsMenu,
          action: IMenuItemAction.showinburger
        }
      },
      {
        dynamic: false,
        click: this.toggleElementMenu,
        feature: AsgardFeatureNames.SCREENSHOT_READER,
        icon: featureIcons.get(AsgardFeatureNames.SCREENSHOT_READER),
        expandable: false,
        title: 'TOOLSMENU.SCREENSHOT_READER',
        isChild: true,
        style: {
          width: ResponsiveUIBreakpoints.ToolsMenu,
          action: IMenuItemAction.showinburger
        }
      },
    ];
    this.LangsMenuItems = [
      {
        dynamic: false, click: this.openLookup,
        feature: AsgardFeatureNames.LOOK_UP,
        icon: featureIcons.get(AsgardFeatureNames.LOOK_UP),
        expandable: false, tooltip: 'TOP_BAR_BTNS.LOOK_UP',
        isChild: false,
        style: {
          width: ResponsiveUIBreakpoints.referenceTools,
          action: IMenuItemAction.hide
        }
      },
      {
        dynamic: false, click: this.openTranslate,
        feature: AsgardFeatureNames.TRANSLATE,
        icon: featureIcons.get(AsgardFeatureNames.TRANSLATE),
        expandable: false, tooltip: 'TOP_BAR_BTNS.TRANSLATE',
        isChild: false,
        style: {
          width: ResponsiveUIBreakpoints.referenceTools,
          action: IMenuItemAction.hide
        }
      },
    ];

    this.ExamModeItems = {
      dynamic: false,
      click: () => this.openExamModePropertiesWindow(),
      feature: AsgardFeatureNames.EXAM_MODE_WINDOW,
      icon: featureIcons.get(AsgardFeatureNames.EXAM_MODE_WINDOW),
      expandable: false,
      tooltip: 'TOP_BAR_BTNS.EXAM_MODE',
      title: 'SETTINGSMENU.EXAM_MODE',
      isChild: false,
      style: {
        width: ResponsiveUIBreakpoints.examModeSettings,
        action: IMenuItemAction.hide
      }
    };

    this.SettingsMenuItems = [
      {
        dynamic: false,
        click: () => this.openSettings(TabNames.Speech),
        feature: AsgardFeatureNames.SETTINGS,
        icon: featureIcons.get(AsgardFeatureNames.SETTINGS),
        expandable: false,
        tooltip: 'TOP_BAR_BTNS.SETTINGS',
        title: 'SETTINGSMENU.SETTINGS',
        isChild: false,
        style: {
          width: ResponsiveUIBreakpoints.settings,
          action: IMenuItemAction.showinburger
        }
      },
      {
        dynamic: false, click: this.logOut,
        feature: AsgardFeatureNames.SIGN_OUT,
        icon: featureIcons.get(AsgardFeatureNames.SIGN_OUT),
        expandable: false, tooltip: 'TOP_BAR_BTNS.LOGOUT',
        title: 'SETTINGSMENU.LOGOUT',
        isChild: false,
        style: {
          width: ResponsiveUIBreakpoints.logout,
          action: IMenuItemAction.showinburger
        }
      },
    ];
    this.ToolbarMenuItems = [
      {
        dynamic: false,
        click: this.toggleElementMenu,
        feature: AsgardFeatureNames.TOOLBAR_MENU,
        icon: featureIcons.get(AsgardFeatureNames.TOOLBAR_MENU),
        expandable: false,
        tooltip: 'TOP_BAR_BTNS.TEXT_NOTES',
        isChild: false,
        style: {
          width: ResponsiveUIBreakpoints.collapseMarkupTools,
          action: IMenuItemAction.collapse
        }
      }
    ];
    this.TextToolsMenuItems = [
      {
        dynamic: false,
        click: this.toggleElementMenu,
        feature: AsgardFeatureNames.MARKUP_MENU,
        icon: featureIcons.get(AsgardFeatureNames.MARKUP_MENU),
        expandable: false,
        tooltip: 'TOP_BAR_BTNS.TEXT_NOTES',
        isChild: false,
        style: {
          width: ResponsiveUIBreakpoints.selectionMode,
          action: IMenuItemAction.hide
        }
      }
    ];
    this.HighlightsMenuItems = [
      {
        dynamic: false,
        click: this.toggleElementMenu,
        feature: AsgardFeatureNames.HIGHLIGHT_MENU,
        icon: featureIcons.get(AsgardFeatureNames.HIGHLIGHT_MENU),
        expandable: false,
        tooltip: 'TOP_BAR_BTNS.MARKERS',
        isChild: false
      }
    ];
    this.EpubAnnotationsMenuItems = [
      {
        dynamic: false,
        click: this.toggleElementMenu,
        feature: AsgardFeatureNames.EPUB_MENU,
        icon: featureIcons.get(AsgardFeatureNames.EPUB_MENU),
        expandable: false,
        tooltip: 'TOP_BAR_BTNS.EPUB_ANNOTATIONS',
        isChild: false
      }
    ];
    this.SRMenuItems = [
      {
        dynamic: false,
        click: this.toggleElementMenu,
        feature: AsgardFeatureNames.SPEECH_RECOGNITION,
        icon: featureIcons.get(AsgardFeatureNames.SPEECH_RECOGNITION),
        expandable: false,
        tooltip: 'TOP_BAR_BTNS.SPEECH_RECOGNITION',
        title: 'SPEECH_RECOGNITION_MENU.SPEECH_RECOGNITION',
        isChild: false,
        style: {
          width: ResponsiveUIBreakpoints.speechRecognition,
          action: IMenuItemAction.showinburger
        }
      }
    ];
    this.DrawingToolsMenuItems = [
      {
        dynamic: false,
        click: this.toggleElementMenu,
        feature: AsgardFeatureNames.DRAWING_TOOLS,
        icon: featureIcons.get(AsgardFeatureNames.DRAWING_TOOLS),
        expandable: false,
        tooltip: 'TOP_BAR_BTNS.DRAWING_TOOLS',
        isChild: false
      }
    ];
    this.ReadingControlMenuItems = [
      {
        dynamic: true,
        feature: AsgardFeatureNames.START_READ_UNIT,
        component: ReadingToolbarComponent,
        expandable: false,
        tooltip: 'TOP_BAR_BTNS.READING_MODES',
        isChild: false
      }
    ];
    this.ScreenshotReaderMenuItems = [
      {
        dynamic: false,
        click: this.toggleElementMenu,
        feature: AsgardFeatureNames.SCREENSHOT_READER,
        icon: featureIcons.get(AsgardFeatureNames.SCREENSHOT_READER),
        expandable: false,
        tooltip: 'TOP_BAR_BTNS.SCREENSHOT_READER',
        isChild: true
      }
    ];

    this.filesMenuElements = [
      this.newTabMenuItem,
      this.openStiMenuItem,
      this.openDriveMenuItem,
      this.saveToDriveMenuItem,
      this.saveAsToDriveMenuItem,
      this.openDocMenuItem,
      // this.openDodpMenuItem,
      this.downloadMenuItem,
      this.printMenuItem,
      this.closeDocumentMenuItem,
    ];

    this.ocrMenuElements = [
      {
        dynamic: true, feature: AsgardFeatureNames.OCR_ON_FILE, icon: featureIcons.get(AsgardFeatureNames.OCR_ON_FILE),
        component: UploadFileComponent,
        componentData: {
          UPLOAD_TYPES: UPLOAD_TYPES.OCR,
          ACCEPTED_FORMATS: ACCEPTED_FORMATS.OCR,
          ACTION: OpenFileAction.OCR
        },
        expandable: false,
        isChild: true
      },
      {
        dynamic: false,
        title: 'MAINMENU.SCANOCRMENU.SCANOCRDRIVE',
        click: this.scanOcrFromDrive,
        feature: AsgardFeatureNames.SCAN_OCR_DRIVE,
        icon: featureIcons.get(AsgardFeatureNames.SCAN_OCR_DRIVE),
        expandable: false,
        isChild: true
      },
      {
        dynamic: false,
        title: 'MAINMENU.SCANOCRMENU.SCANOCEROPENFILE',
        click: this.scanOcrFromOpenDocument,
        feature: AsgardFeatureNames.SCAN_OCR_OPENFILE,
        icon: featureIcons.get(AsgardFeatureNames.SCAN_OCR_OPENFILE),
        expandable: false,
        isChild: true
      },
    ];

    this.filesMenuItem = {
      dynamic: false,
      feature: AsgardFeatureNames.MAINMENUFILES,
      expandable: false,
      title: 'MAINMENU.SMALLMENU.FILES',
      icon: featureIcons.get(AsgardFeatureNames.MAINMENUFILES),
      isChild: true,
      children: this.filesMenuElements
    }

    this.ocrMenuItem = {
      dynamic: false,
      feature: AsgardFeatureNames.MAINMENUOCR,
      expandable: false,
      title: 'MAINMENU.SMALLMENU.OCR',
      icon: featureIcons.get(AsgardFeatureNames.MAINMENUOCR),
      isChild: true,
      children: this.ocrMenuElements,
    }

    this.toolsMenuItem = {
      dynamic: false,
      feature: AsgardFeatureNames.MAINMENUTOOLS,
      expandable: false,
      title: 'MAINMENU.SMALLMENU.TOOLS',
      icon: featureIcons.get(AsgardFeatureNames.MAINMENUTOOLS),
      isChild: true,
      children: this.ToolsMenuElements.concat(this.SRMenuItems),
    };

    this.searchMenuItem = {
      dynamic: false,
      click: this.toggleElementMenu,
      feature: AsgardFeatureNames.SEARCH,
      icon: featureIcons.get(AsgardFeatureNames.SEARCH),
      expandable: false,
      title: 'TOOLSMENU.SEARCH',
      isChild: false,
      style: {
        width: ResponsiveUIBreakpoints.SearchMenu,
        action: IMenuItemAction.showinburger
      }
    };

    // normal main menu part I
    this.normalMainMenuElements = [
      this.newTabMenuItem,
      this.openStiMenuItem,
      this.openDriveMenuItem,
      this.saveToDriveMenuItem,
      this.saveAsToDriveMenuItem,
      this.openDocMenuItem,
      // this.openDodpMenuItem,
      this.downloadMenuItem
    ];

    // small main menu part I
    this.smallMainMenuElements = [this.filesMenuItem];

    // kes elements in menu's
    if (isK3000domain()) {
      this.normalMainMenuElements = this.normalMainMenuElements.concat(this.KESMenuElements);
      this.smallMainMenuElements = this.smallMainMenuElements.concat(this.KESMenuElements);
    }

    // normal main menu part II
    this.normalMainMenuElements = this.normalMainMenuElements.concat(this.printMenuItem);
    this.normalMainMenuElements = this.normalMainMenuElements.concat(this.ocrMenuElements);
    this.normalMainMenuElements = this.normalMainMenuElements.concat(this.closeDocumentMenuItem);

    // small main menu part II
    this.smallMainMenuElements = this.smallMainMenuElements.concat([this.ocrMenuItem]);
  }

  getMenuByFeature(menuItem: AsgardFeatureNames): RootMenuItem | undefined {
    return this.AllMenus.find(e => e.feature === menuItem);
  }

  toggleElementMenu = (el: AsgardFeatureNames) => {
    if (el === AsgardFeatureNames.SCREENSHOT_READER) { this.screenshotSvc.takeScreenshot(); }
    this.uiService.toggleElement(el);
    if (el === AsgardFeatureNames.SEARCH) {
      // Couldn't find a way to do this with @ViewChild from search component.
      document.getElementById('searchTxtField')?.focus();
    }
  }

  getMainMenuElements(): RootMenuItem[] {
    // TODO: FOR NOW BOTH PROJECT SHOULD HIDE KES/PDF Features in menu
    // Dont change this lines please
    // const arr: RootMenuItem[] = [...this.MainMenuElements];

    // TODO: FOR NOW BOTH PROJECT SHOULD HIDE KES/PDF Features in menu
    // if (!isK3000domain()) {
    //   arr.splice(6, 2);
    //   return arr;
    // }

    // return arr;

    //since 14/12/2020
    return this.displaySmallMainMenu ? [...this.smallMainMenuElements] : [...this.normalMainMenuElements];
  }

  getToolsMenuElements(): RootMenuItem[] {
    return this.ToolsMenuElements;
  }

  getToolbarMenuItem(): RootMenuItem | undefined {
    return this.getMenuByFeature(AsgardFeatureNames.TOOLBAR_MENU);
  }

  getTextToolsMenuItem(): RootMenuItem | undefined {
    return this.getMenuByFeature(AsgardFeatureNames.MARKUP_MENU);
  }

  getHighlightsMenuItem(): RootMenuItem | undefined {
    return this.getMenuByFeature(AsgardFeatureNames.HIGHLIGHT_MENU);
  }

  getEpubMenuItem(): RootMenuItem | undefined {
    return this.getMenuByFeature(AsgardFeatureNames.EPUB_MENU);
  }

  getScreenshotReaderMenuItem(): RootMenuItem | undefined {
    return this.getMenuByFeature(AsgardFeatureNames.SCREENSHOT_READER);
  }

  getReadingControlMenuItems(): RootMenuItem | undefined {
    return this.getMenuByFeature(AsgardFeatureNames.START_READ_UNIT);
  }

  getDrawingToolsMenuItem(): RootMenuItem | undefined {
    return this.getMenuByFeature(AsgardFeatureNames.DRAWING_TOOLS);
  }

  getSRMenuItem(): RootMenuItem | undefined {
    return this.getMenuByFeature(AsgardFeatureNames.SPEECH_RECOGNITION);
  }

  getSearchMenuItem(): RootMenuItem | undefined {
    return this.getMenuByFeature(AsgardFeatureNames.SEARCH);
  }

  getLangsMenuItems(): RootMenuItem[] {
    return this.LangsMenuItems;
  }

  getExamModeMenuItem(): RootMenuItem | undefined {
    return this.ExamModeItems;
  }

  getSettingsMenuItems(): RootMenuItem[] {
    return this.SettingsMenuItems;
  }

  getCollapsedMenuItems(): RootMenuItem[] {
    return (this.CollapsedFeaturesMenuItems || []);
  }

  setDisplaySmallMainMenu(displaySmallMainMenu: boolean): void {
    // only when the menu should change, should it actually change
    if (this.displaySmallMainMenu === displaySmallMainMenu) { return }

    // save displaySmallMainMenu
    this.displaySmallMainMenu = displaySmallMainMenu;

    // emit update
    this.menuItemsUpdated.emit();
  }

  toggleDisplaySmallMainMenu(displayIconOnly: boolean): void {
    this.displaySmallMenuIconOnly = displayIconOnly;
    this.getMainMenuElements().forEach((e: RootMenuItem) => {
      e.isChild = !displayIconOnly;
    })
    this.menuItemsUpdated.emit();
  }

  setWidthsForMenuItemsForStapAddOn(): void {
    const settings = this.SettingsMenuItems[0];
    if (settings && settings.style) {
      settings.style.width = ResponsiveUIBreakpoints.settings_stap_addOn;
    }

    const logout = this.SettingsMenuItems[1];
    if (logout && logout.style) {
      logout.style.width = ResponsiveUIBreakpoints.logout_stap_addOn;
    }

    const toolBar = this.ToolbarMenuItems[0];
    if (toolBar && toolBar.style) {
      toolBar.style.width = ResponsiveUIBreakpoints.collapseMarkupTools_stap_addOn;
    }
  }

  private init = () => {
    this.reopenCloudDlgSub();
    this.subToChangingTab();
  }

  private reopenCloudDlgSub(): void {
    this.modalService.reopeningDlg.subscribe(() => {
      this.modalService.createModalWindow(FileManagerDialogComponent, { reopened: true });
    });
  }

  private subToChangingTab(): void {
    this.docMgrService.currentDocumentChanged
      .subscribe((docId: string | null) => {
        const doc = this.docMgrService.getDocument(docId);
        if (doc && doc.isDocumentReady) {
          this.toggleEditorToolbar(docId);
        } else if (docId === null && this.tabService.activeTab === null) { // no tabs opened
          this.toggleEditorToolbar(null);
        }
      });
    this.docMgrService.documentReady
      .subscribe((docId: string | null) => {
        this.toggleEditorToolbar(docId);
      });
  }

  private toggleEditorToolbar(docId: string | null): void {
    // every featrue that is toggleble, no matter the open doc
    const toggledFeatures = [
      AsgardFeatureNames.SAVE_AS_TO_DRIVE,
      AsgardFeatureNames.SAVE_TO_DRIVE,
      AsgardFeatureNames.DOWNLOAD,
      AsgardFeatureNames.PRINT,
      AsgardFeatureNames.EXPORT_KES,
      AsgardFeatureNames.EXPORT_KES_DRIVE,
      AsgardFeatureNames.SCAN_OCR_OPENFILE,
      AsgardFeatureNames.CLOSE_DOCUMENT,
      AsgardFeatureNames.AUDIO_MAKER,
      AsgardFeatureNames.SCREENSHOT_READER,
      AsgardFeatureNames.TOOLBAR_MENU,
      AsgardFeatureNames.DRAWING_TOOLS,
      AsgardFeatureNames.HIGHLIGHT_MENU,
      AsgardFeatureNames.EPUB_MENU,
    ];

    // if there is no docId, then there is no doc open
    // disable everything that can be toggled
    if (docId === null) {
      this.ngZone.run(() => {
        this.uiService.setItemUnActive(toggledFeatures);
        this.uiService.disableState(toggledFeatures);
      });
      return;
    }

    if (this.docMgrService.currentDoc && !this.docMgrService.documentReady) {
      return;
    }

    let featuresThatShouldNotBeEnabled: AsgardFeatureNames[] = [];
    const doc = this.docMgrService.getDocument(docId);
    if (doc) {
      if (doc instanceof AsgardDocPdf) {
        featuresThatShouldNotBeEnabled = [
          AsgardFeatureNames.EPUB_MENU
        ]
      } else if (doc instanceof AsgardDocFroala) {
        featuresThatShouldNotBeEnabled = [
          AsgardFeatureNames.EXPORT_KES,
          AsgardFeatureNames.EXPORT_KES_DRIVE,
          AsgardFeatureNames.SCAN_OCR_OPENFILE,
          AsgardFeatureNames.SCREENSHOT_READER,
          AsgardFeatureNames.TOOLBAR_MENU,
          AsgardFeatureNames.DRAWING_TOOLS,
          AsgardFeatureNames.HIGHLIGHT_MENU,
          AsgardFeatureNames.EPUB_MENU,
        ];
      } else if (doc instanceof AsgardDocEpub) {
        featuresThatShouldNotBeEnabled = [
          AsgardFeatureNames.PRINT,
          AsgardFeatureNames.EXPORT_KES,
          AsgardFeatureNames.EXPORT_KES_DRIVE,
          AsgardFeatureNames.SCAN_OCR_OPENFILE,
          AsgardFeatureNames.AUDIO_MAKER,
          AsgardFeatureNames.SCREENSHOT_READER,
          AsgardFeatureNames.TOOLBAR_MENU,
          AsgardFeatureNames.DRAWING_TOOLS,
        ];
      }

      // do not allow audio maker for dedicon files
      const activeTab = this.tabService.activeTab;
      if (activeTab && docId === activeTab.id) {
        if (activeTab.isDedicon) {
          featuresThatShouldNotBeEnabled.push(AsgardFeatureNames.AUDIO_MAKER);
        }
        if (activeTab.isSTI) {
          featuresThatShouldNotBeEnabled.push(...[
            AsgardFeatureNames.DOWNLOAD,
            AsgardFeatureNames.SAVE_TO_DRIVE,
            AsgardFeatureNames.SAVE_AS_TO_DRIVE,
            AsgardFeatureNames.PRINT,
            AsgardFeatureNames.SCAN_OCR_OPENFILE,
            AsgardFeatureNames.EXPORT_KES,
            AsgardFeatureNames.EXPORT_KES_DRIVE,
          ]);
        }
      }

      // make sure everything that is not allowed is inactive and disabled
      this.uiService.setItemUnActive(featuresThatShouldNotBeEnabled);
      this.uiService.disableState(featuresThatShouldNotBeEnabled);
      // enable everything that is left
      this.uiService.enableState(toggledFeatures.filter((x) => featuresThatShouldNotBeEnabled.indexOf(x) < 0));   
    } else {
      console.error('No document found for toggling editor toolbar');
    }
  }

  private openTab = async () => {
    this.tabService.addNewTab();
  }

  private openFromDrive = async () => {
    this.cloudManager.setPickerStatus(SaveStatusEnum.Open);
    this.modalService.createModalWindow(FileManagerDialogComponent);
  }

  private saveToDrive = async () => {
    const tab = this.tabService.activeTab;
    if (!tab) {
      return;
    }

    await this.autosave.removeUnsavedChangesAndFile(tab.id, false);

    if (
      (this.cloudManager.activeCloud === CloudServiceEnum.GoogleDrive && tab.gDriveMetadata !== null) ||
      (this.cloudManager.activeCloud === CloudServiceEnum.DropBox && tab.dropboxMetadata !== null) ||
      (this.cloudManager.activeCloud === CloudServiceEnum.OneDrive && tab.oneDriveMetadata !== null)
    ) { // Go to save as on first time save to drive (from local)
      this.cloudManager.SaveToDrive.emit();
      this.cloudManager.setSaveAs(false);
    } else {
      this.saveAsToDrive();
    }
  }

  private saveAsToDrive = async () => {
    const tab = this.tabService.activeTab;
    if (!tab) {
      return;
    }
    await this.autosave.removeUnsavedChangesAndFile(tab.id, false);
    this.cloudManager.setPickerStatus(SaveStatusEnum.Save);
    this.modalService.createModalWindow(FileManagerDialogComponent);
    this.cloudManager.setSaveAs(true);
    this.cloudManager.setKesToCloud(false);
  }

  private downloadDoc = async () => {
    const tab = this.tabService.activeTab;

    if (!tab) {
      return;
    }

    if (this.docMgrService.currentDoc) {
      const readingpos = this.docMgrService.currentDoc.getReadingPostition();
      const readingPageIdx = this.docMgrService.currentDoc.getCurrentPageIdx();
      if (this.docMgrService.currentDoc instanceof AsgardDocPdf) {
        this.saveService.downloadPDF(tab.id, readingpos, readingPageIdx);
      } else if (this.docMgrService.currentDoc instanceof AsgardDocFroala) {
        const translatedTab = this.translate.instant(tab.title);
        // this.convertorService.getHtmlAndCreateRtf(FileActionEnum.Download, translatedTab, readingpos);
        this.convertorService.makeConversion<HtmlToRtfConversion>(HtmlToRtfConversion, {
          action: FileActionEnum.Download,
          fileName: translatedTab,
          readingItemIdx: readingpos || undefined,
        });
      } else if (this.docMgrService.currentDoc instanceof AsgardDocEpub) {
        this.saveService.downloadEpubWithAnnotations();
      }
    }

    await this.autosave.removeUnsavedChangesAndFile(tab.id);
  }

  private printDocument = async () => {
    const tab = this.tabService.activeTab;
    if (!tab) {
      return;
    }
    this.printService.print();
  }

  private exportPdfToKes = async () => {
    const tab = this.tabService.activeTab;
    if (!tab) {
      return;
    }
    this.convertorService.makeConversion<PdfToKesConversion>(PdfToKesConversion, { action: FileActionEnum.ExportKes });
  }

  private exportKesToDrive = async () => {
    this.cloudManager.setKesToCloud(true);
    this.cloudManager.setPickerStatus(SaveStatusEnum.Save);
    this.modalService.createModalWindow(FileManagerDialogComponent);
  }

  private logOut = async () => {
    // if create exam mode was active, cancel it
    if (this.examModeRestrictionsService.isExamCreationActive) {
      this.examCreationService.endCreation();
    }

    if(this.examModeRestrictionsService.isExamInProgress){
      this.showWarningStudentLogout();
    }else {
      await this.examModeService.dispose();

      const unsavedChanged = await this.autosave.beforeCloseCheckForUnsavedChanges();
      if (unsavedChanged) {
        const result = await this.tabService.prepareCloseTab(true);
        if (result) {
          await this.authService.logout();
        }
      } else {
        await this.authService.logout();
      }
    }
  }

  private openSettings = async (openTab: string | null = null) => {
    if (openTab) {
      this.menuTabService.changeTab(openTab);
    }
    this.modalService.createModalWindow(SettingsComponent, { messageKey: openTab });
  }

  private openLookup = async () => {
    this.modalService.createModalWindow(LookupComponent);
  }

  private openTranslate = async () => {
    this.asgardTranslateService.openTranslateTab();
  }

  private scanOcrFromDrive = async () => {
    this.cloudManager.setPickerStatus(SaveStatusEnum.Ocr);
    this.modalService.createModalWindow(FileManagerDialogComponent);
  }

  private scanOcrFromOpenDocument = async () => {
    if (this.tabService.activeTab !== null) {
      const loadedFile: File = this.tabService.activeTab.file;
      const currentPageMode = true;
      this.modalService.createModalWindow(PdfPageSelectDialogComponent, { loadedFile, currentPageMode });
    }
  }

  private closeCurrentDocument = async () => {
    if (this.tabService.activeTab) {
      const tab = this.tabService.activeTab;
      const unsavedChanged = await this.autosave.isDocumentUnsavedChanges(tab.id);
      if (unsavedChanged) {
        const result = await this.tabService.prepareCloseTab(unsavedChanged ? true : false);
        if (result) {
          await this.autosave.removeUnsavedChangesAndFile(tab.id);
          this.tabService.closeTab(tab.id);
        }
      } else {
        await this.autosave.removeUnsavedChangesAndFile(tab.id);
        this.tabService.closeTab(tab.id);
      }
    }
  }

  // private openFromDodp = async () => {
  //   this.cloudManager.setPickerStatus(SaveStatusEnum.Open);
  //   this.modalService.createModalWindow(DodpStorageDialogComponent);
  // }

  private openFromSti = async () => {
    this.modalService.closeModal();
    this.dialog.open(StiDialogComponent, { autoFocus: 'asg-file-overview-table' });
  }

  private openExamModePropertiesWindow = async () => {
    // check if the browser is supported
    // if not, show toastr with warning
    if (!this.platform.isBrowserSupported) {
      this.toastrService.showToaster(ToastrActionEnum.Warning, this.translate.instant('UNSUPPORTED_BROWSER.TOASTR_MESSAGE'));
      return;
    }

    // check if Asgard is in 'test exam' mode
    // if it is, it does not matter it the user is a supervisor or not
    // always toggle the exam floating toolbar
    if (this.examModeRestrictionsService.isTestModeActive) {
      this.toggleElementMenu(AsgardFeatureNames.EXAM_MODE_FLOATING_TOOLBAR);
      return;
    }

    const isSupervisor = await this.examModeRestrictionsService.isSupervisor();
    const isUser = await this.examModeRestrictionsService.isUser();
    const tabs = this.tabService.allTabs;

    // if the user is a supervisor
    if (isSupervisor) {
      // check if the supervisor was already creating an exam
      if (this.uiService.checkElementState(AsgardFeatureNames.EXAM_CREATION_FLOATING_TOOLBAR)) {
        // if active, end the creation
        this.examCreationService.endCreation();
      } else {
        // check if there is a document open
        // if there is nothing open, 
        // toggle the floating toolbar to view the exams and completed exams
        if (tabs.length === 0) {
            this.modalService.createModalWindow(OverviewExamsComponent, {});
            this.examModeRestrictionsService.activateExamCreation();
        } else {
          // if there are other or multiple documents open
          await this.examModeRestrictionsService.warnSupervisorAboutClosingDocuments();
        }
      }
    }

    // if the user is a regular user (student)
    else if (isUser) {
      // if the user is making an exam, toggle the floating toolbar
      if (this.examModeRestrictionsService.isExamInProgress) {
        this.toggleElementMenu(AsgardFeatureNames.EXAM_MODE_FLOATING_TOOLBAR);
      } else {
        // otherwise, check if there are documents open
        if (tabs.length > 0) {
          // if there are, warn the user he/she must close them
          await this.examModeRestrictionsService.warnUserAboutClosingDocuments();
        } else {
          // if there are not, display the dialog to enter an Access Code
          this.modalService.createModalWindow(AccessCodeDialogComponent);
        }
      }
    }
  }


  public addMenuItemToBurger(feautre: AsgardFeatureNames): void {
    this.addMenuItemToSmallBurger(feautre);
    this.addMenuItemToNormalBurger(feautre);
    this.menuItemsUpdated.emit();
  }

  public removeItemFromBurger(feautre: AsgardFeatureNames): void {
    this.removeItemFromSmallBurger(feautre);
    this.removeItemFromNormalBurger(feautre);
    this.menuItemsUpdated.emit();
  }

  public collapseFeatures() {
    if (this.CollapsedFeaturesMenuItems.length === 0) {
      this.FeaturesToCollapse.forEach(f => {
        const elem = this.getMenuByFeature(f);
        if (elem) {
          this.CollapsedFeaturesMenuItems.push(elem);
        }
      });
      this.menuItemsUpdated.emit();
    }
  }

  public unCollapseFeatures() {
    // console.log('unCollapseFeatures', this.CollapsedFeaturesMenuItems);
    if (this.CollapsedFeaturesMenuItems.length !== 0) {
      this.CollapsedFeaturesMenuItems = [];
      this.menuItemsUpdated.emit();
    }
  }

  private addMenuItemToSmallBurger(feature: AsgardFeatureNames) {
    // if feature is one of the tools features, don't add feature to main menu, but add tools menu instead
    if (feature === AsgardFeatureNames.AUDIO_MAKER ||
      feature === AsgardFeatureNames.WORD_PREDICTION ||
      feature === AsgardFeatureNames.SPELL_CHECK ||
      feature === AsgardFeatureNames.SCREENSHOT_READER
    ) {
      // check if tools menu item is already added
      const toolsElement = this.smallMainMenuElements.find(e => e.feature === AsgardFeatureNames.MAINMENUTOOLS);
      if (!toolsElement) {
        // if not, add it underneath ocr menu
        const index = this.smallMainMenuElements.findIndex(e => e.feature === AsgardFeatureNames.MAINMENUOCR);
        if (index !== -1) {
          // if tools menu was added, 'speech recognition' should be 'moved' from the main menu to the tools menu
          this.removeItemFromSmallBurger(AsgardFeatureNames.SPEECH_RECOGNITION);
          // .isChild is set to false when removing from burger, but should stay true or the title is not displayed
          // in the toolsmenu
          const srElem = this.getMenuByFeature(AsgardFeatureNames.SPEECH_RECOGNITION);
          if (srElem) {
            srElem.isChild = true;
          }

          // add the tools menu to the main menu
          this.smallMainMenuElements.splice(index + 1, 0, this.toolsMenuItem);
        }
      }
      // do not continue with rest of function
      return;
    }

    // if the tools menu is in the main task bar, then speech_recognition should be added to the main menu
    // if the tools menu is in the main menu, then speech_recoginition is already in the tools submenu
    if (feature === AsgardFeatureNames.SPEECH_RECOGNITION) {
      const toolsElement = this.smallMainMenuElements.find(e => e.feature === AsgardFeatureNames.MAINMENUTOOLS);
      // do not continue with rest of function
      if (toolsElement) { return; }
    }

    // if it's any other feature
    const elem = this.smallMainMenuElements.find(e => e.feature === feature);
    if (!elem) {
      const elemToAdd = this.getMenuByFeature(feature);

      if (elemToAdd) {
        const itemIndexInsertedBefore = this.smallMainMenuElements.findIndex(e => !!(e.style && elemToAdd && elemToAdd.style && (e.style.width > elemToAdd.style.width)));
        if (itemIndexInsertedBefore !== -1) {
          this.smallMainMenuElements.splice(itemIndexInsertedBefore, 0, elemToAdd);
        } else {
          this.smallMainMenuElements.push(elemToAdd);
        }
        elemToAdd.isChild = this.displaySmallMenuIconOnly ? false : true;
      }
    }
  }

  private addMenuItemToNormalBurger(feature: AsgardFeatureNames): void {
    const elem = this.normalMainMenuElements.find(e => e.feature === feature);

    if (!elem) {
      const elemToAdd = this.getMenuByFeature(feature);

      if (elemToAdd) {
        const itemIndexInsertedBefore = this.normalMainMenuElements.findIndex(e => !!(e.style && elemToAdd && elemToAdd.style && (e.style.width > elemToAdd.style.width)));
        if (itemIndexInsertedBefore !== -1) {
          this.normalMainMenuElements.splice(itemIndexInsertedBefore, 0, elemToAdd);
        } else {
          this.normalMainMenuElements.push(elemToAdd);
        }

        // NOTE: 22/01/2021
        // the items in the tools menu are a bit of a special case, because they can be located on 3 different locations
        // (speech recognition can be located in 4 different locations)
        // when the screen width becomes smaller then a certain size, this function is passed to add the items to the normal main menu
        // BUT for certain items (like the settings for example) there is a scenario where the title should not be displayed
        // for the tools menu items however, if they are displayed in a submenu of the tools menu, they should always display their title
        // therefor isChild should be (and stay) true in that situation for those features
        const toolsElement = this.smallMainMenuElements.find(e => e.feature === AsgardFeatureNames.MAINMENUTOOLS);
        if (toolsElement && this.displaySmallMenuIconOnly
          && (feature === AsgardFeatureNames.AUDIO_MAKER
            || feature === AsgardFeatureNames.WORD_PREDICTION
            || feature === AsgardFeatureNames.SPELL_CHECK
            || feature === AsgardFeatureNames.SCREENSHOT_READER
            || feature === AsgardFeatureNames.SPEECH_RECOGNITION)
        ) {
          elemToAdd.isChild = true;
        } else {
          elemToAdd.isChild = this.displaySmallMenuIconOnly ? false : true;
        }
      }
    }
  }

  private removeItemFromSmallBurger(feature: AsgardFeatureNames): void {
    // if feature is one of the tools features, don't remove feature from main menu, but remove tools menu instead
    if (feature === AsgardFeatureNames.AUDIO_MAKER ||
      feature === AsgardFeatureNames.WORD_PREDICTION ||
      feature === AsgardFeatureNames.SPELL_CHECK ||
      feature === AsgardFeatureNames.SCREENSHOT_READER
    ) {
      // check if tools menu item is already removed
      const toolsElement = this.smallMainMenuElements.find(e => e.feature === AsgardFeatureNames.MAINMENUTOOLS);
      if (toolsElement) {
        // get index and remove it
        const index = this.smallMainMenuElements.findIndex(e => e.feature === AsgardFeatureNames.MAINMENUTOOLS);
        if (index !== -1) {
          this.smallMainMenuElements.splice(index, 1);

          // if tools menu was removed, 'speech recognition' should be moved from the tools menu to the main menu
          this.addMenuItemToSmallBurger(AsgardFeatureNames.SPEECH_RECOGNITION);
        }
      }
      // do not continue with function
      return;
    }

    // if any other feature
    const item = this.smallMainMenuElements.find(e => e.feature === feature);
    if (item) {
      const index = this.smallMainMenuElements.findIndex(e => e.feature === feature);

      if (index !== -1) {
        this.smallMainMenuElements.splice(index, 1);

        const elem = this.getMenuByFeature(feature);
        if (elem) {
          elem.isChild = false;
        }
      }
    }
  }

  private removeItemFromNormalBurger(feature: AsgardFeatureNames): void {
    const item = this.normalMainMenuElements.find(e => e.feature === feature);
    if (item) {
      const index = this.normalMainMenuElements.findIndex(e => e.feature === feature);

      if (index !== -1) {
        this.normalMainMenuElements.splice(index, 1);

        const elem = this.getMenuByFeature(feature);
        if (elem) {
          elem.isChild = false;
        }
      }
    }
  }

  private showWarningStudentLogout() {
    const data: MessageDialogData = {
      title: 'EXAM_LOGOUT_WARNING.TITLE',
      message: 'EXAM_LOGOUT_WARNING.MESSAGE',
      actions: [
        {
          title: 'EXAM_LOGOUT_WARNING.OK',
          color: MessageDialogActionColor.Primary,
          handler: () => this.modalService.createModalWindow(CompletedExamPreviewDialogComponent, {})
        },
        {
          title: 'EXAM_LOGOUT_WARNING.NO',
          color: MessageDialogActionColor.None,
          handler: () => { }
        }
      ],
    };

    this.dialog.open(MessageDialogComponent, { data, disableClose: true });
  }
}
